/**
 * `/settings/billing` — Polar-backed billing page (Phase 6).
 *
 * Three render states driven by `useSubscription()`:
 *
 *   1. **No subscription** (`polar.subscription_id === null`)
 *        Render the four plan cards. "Choose X" on each paid card
 *        calls `POST /v1/billing/checkout`, then navigates the
 *        browser to Polar's hosted checkout URL.
 *
 *   2. **Active subscription** (`polar.status === 'active'`)
 *        Show the current-plan banner + renewal date.
 *        - Other paid cards become "Change plan" CTAs that POST to
 *          `/v1/billing/change-plan` (immediate proration).
 *        - "Manage billing" button opens the Polar customer portal
 *          (`GET /v1/billing/portal`) in a new tab.
 *
 *   3. **Canceled / past_due** — same card grid as (1) plus a banner
 *        recapping the lapsed state.
 *
 * Post-change polling: after `change-plan` returns 200, we invalidate
 * `/v1/usage` + `/v1/workspace` on a 1s interval for up to 15s,
 * stopping the moment the server-reported `plan` differs from where
 * we started. The Polar → webhooks → Supabase pipeline almost always
 * lands in under 10 s; 15 s is the safety valve. On timeout we surface
 * a "Change sent — refresh in a moment" toast.
 *
 * No confirmation dialog on change-plan. Polar applies proration both
 * directions (upgrade = charge now, downgrade = credit) and both are
 * reversible with one more click, so a modal adds friction without
 * buying anything. Button enters a "Changing plan…" loading state
 * for the duration of the poll.
 *
 * Deep-link: `/settings/billing?plan=<slug>&src=www`. The pricing page
 * on apps/www uses this. When we see a `?plan=` on mount AND the org
 * has no subscription, we auto-initiate checkout for that plan — one
 * click on the marketing CTA takes the user through sign-in (if
 * needed) and straight into Polar's hosted checkout.
 */
import { ErrorState } from '@/components/primitives/ErrorState';
import { UsageCard } from '@/components/primitives/UsageCard';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toastFromError, toastSuccess } from '@/lib/errors';
import type { BillingPlanOption, PlanSlug, UsageResponse } from '@/lib/queries';
import {
  PLAN_SLUGS,
  invalidateSubscription,
  invalidateUsage,
  invalidateWorkspace,
  planChangeDirection,
  usageKeys,
  useActiveOrg,
  useBilling,
  useBillingPlans,
  useChangePlanMutation,
  useCheckoutMutation,
  useOpenPortalMutation,
  useSubscription,
  useUsage,
  useWorkspace,
} from '@/lib/queries';
import { cn } from '@/lib/utils';
import { useUser } from '@clerk/react';
import { ArrowSquareOut, CheckCircle, Crown } from '@phosphor-icons/react';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect, useRef, useState } from 'react';

interface BillingSearch {
  /**
   * Deep-link target plan from apps/www pricing page. Read once on
   * mount; if the user has no active subscription we auto-start a
   * Polar checkout for this plan.
   */
  plan?: PlanSlug;
  /**
   * Marketing attribution. Currently unused at runtime; logged by
   * the query's X-Request-Id for analytics, kept here so the route
   * doesn't refuse to parse the param.
   */
  src?: string;
  /**
   * Polar checkout redirect marker. We set `success_url` /
   * `cancel_url` to land back here with one of these so the page
   * can show a toast + refetch.
   */
  polar_checkout?: 'success' | 'cancel';
}

export const Route = createFileRoute('/_app/settings/billing')({
  validateSearch: (search): BillingSearch => {
    const plan = typeof search.plan === 'string' ? search.plan.toLowerCase() : undefined;
    return {
      plan:
        plan && (PLAN_SLUGS as readonly string[]).includes(plan) ? (plan as PlanSlug) : undefined,
      src: typeof search.src === 'string' ? search.src.slice(0, 32) : undefined,
      polar_checkout:
        search.polar_checkout === 'success' || search.polar_checkout === 'cancel'
          ? search.polar_checkout
          : undefined,
    };
  },
  component: BillingPage,
});

function BillingPage(): ReactElement {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { orgId } = useActiveOrg();
  const qc = useQueryClient();
  const billing = useBilling();
  const subscription = useSubscription();
  const plans = useBillingPlans();
  const usage = useUsage();
  const workspace = useWorkspace();
  // Clerk user — we forward `primaryEmailAddress` to Polar on every
  // checkout so the resulting Polar customer row has a deliverable
  // address on file. Polar locks `customer_email` on the hosted
  // checkout page once it's supplied, which is why we can't rely on
  // the buyer editing a synthetic fallback at Polar's end. See
  // `apps/api/src/routes/billing.ts` CheckoutBodySchema for the
  // server-side handling of this field.
  const { user } = useUser();

  const checkout = useCheckoutMutation();
  const changePlan = useChangePlanMutation();
  const openPortal = useOpenPortalMutation();

  const [pendingPlan, setPendingPlan] = useState<PlanSlug | null>(null);

  const activeOrg = workspace.data?.organisations.find((o) => o.auth_provider_id === orgId);
  const polar = subscription.data?.polar;
  const currentPlanSlug = (subscription.data?.plan ?? billing.plan ?? null)?.toLowerCase() ?? null;

  // Subscription state detection.
  //
  // The authoritative signal is our own `plan` column (written by the
  // Polar webhook handler on every `subscription.*` event). The Polar
  // `status` field on the joined response is the supplementary
  // lapsed-detection signal — we can't treat `status !== 'active'` as
  // "unsubscribed" because Polar's status enum has several "still
  // subscribed, still billing" values (`trialing`, `past_due`,
  // `incomplete_expired` during grace, …) and the `/v1/billing/subscription`
  // response nulls the whole Polar block when Polar is unreachable
  // (graceful-degradation path in `apps/api/src/routes/billing.ts`).
  //
  // Phase 8 of the Polar migration removed the Clerk-era
  // `subscription_ended_at` anchor. Lapsed detection is now purely
  // from Polar's status (+ `plan === null` after the webhook clears
  // it on terminal events).
  //
  // Definitions:
  //   - `hasPlan`      → our DB says this org is on a paid plan.
  //   - `hasLapsed`    → Polar status is one of the terminal values.
  //                      Overrides `hasPlan` — a lapsed sub may still
  //                      have `plan` set for the short window before
  //                      the terminal webhook lands.
  //   - `isSubscribed` → `hasPlan && !hasLapsed`.
  //   - `isFirstTime`  → no `plan` at all on our row (new org / pre-
  //                      first-checkout). Shows "Choose a plan" grid.
  //
  // Polar status values that count as "still subscribed":
  //   active, trialing, past_due, incomplete
  // Terminal (lapsed):
  //   canceled, revoked, unpaid, incomplete_expired
  // (`past_due` is billing-distressed but Polar is still attempting
  // payment, so we show the subscription as active with a warning —
  // Phase 9 ships the "update your card" banner.)
  const TERMINAL_POLAR_STATUSES = new Set(['canceled', 'revoked', 'unpaid', 'incomplete_expired']);
  const hasPlan = (subscription.data?.plan ?? billing.plan) != null;
  const polarTerminal = polar?.status != null && TERMINAL_POLAR_STATUSES.has(polar.status);
  const hasLapsed = polarTerminal;
  const isSubscribed = hasPlan && !hasLapsed;
  const isFirstTime = !hasPlan;
  const isLapsed = hasLapsed;

  // ── Deep-link checkout ─────────────────────────────────────────────
  //
  // When apps/www routes "Choose Pro" here, it adds `?plan=pro&src=www`.
  // If the visitor is signed-in and has no subscription, immediately
  // kick off a Polar checkout for that plan. The mount ref guards
  // against re-firing on subscription refetch.
  const autoCheckoutFired = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — this runs once per mount/param load
  useEffect(() => {
    if (autoCheckoutFired.current) return;
    if (subscription.isLoading) return;
    if (!search.plan) return;
    // Only auto-fire checkout for orgs with no subscription or a
    // terminally-lapsed one. A currently-subscribed org arriving with
    // `?plan=<slug>` should land on the billing page with the card
    // highlighted — NOT silently move them onto a different plan.
    if (!isFirstTime && !isLapsed) return;
    autoCheckoutFired.current = true;
    void startCheckout(search.plan);
    // Clear the `?plan=` so a browser back-button after Polar doesn't
    // loop us straight into another checkout.
    void navigate({
      to: '/settings/billing',
      search: (prev) => {
        const { plan: _discard, ...rest } = prev as BillingSearch;
        return rest;
      },
      replace: true,
    });
  }, [subscription.isLoading, isFirstTime, isLapsed, search.plan]);

  // ── Polar redirect banner ──────────────────────────────────────────
  //
  // After a completed checkout Polar redirects to
  // `/settings/billing?polar_checkout=success`. Fire a toast + force
  // a refetch so the page flips to the "active subscription" state
  // as soon as the webhook lands. Cancel path is quieter — just a
  // banner, no refetch needed.
  const checkoutBannerShown = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — this runs once per mount/redirect
  useEffect(() => {
    if (checkoutBannerShown.current) return;
    if (!search.polar_checkout) return;
    checkoutBannerShown.current = true;
    if (search.polar_checkout === 'success') {
      toastSuccess('Checkout complete', 'Your subscription will activate in a moment.');
      void invalidateSubscription(qc);
      void invalidateWorkspace(qc);
      void invalidateUsage(qc);
    }
    void navigate({
      to: '/settings/billing',
      search: (prev) => {
        const { polar_checkout: _discard, ...rest } = prev as BillingSearch;
        return rest;
      },
      replace: true,
    });
  }, [search.polar_checkout]);

  // ── Action handlers ────────────────────────────────────────────────

  async function startCheckout(plan: PlanSlug): Promise<void> {
    const origin = window.location.origin;
    const customerEmail = user?.primaryEmailAddress?.emailAddress;
    try {
      setPendingPlan(plan);
      const res = await checkout.mutateAsync({
        plan,
        success_url: `${origin}/settings/billing?polar_checkout=success`,
        cancel_url: `${origin}/settings/billing?polar_checkout=cancel`,
        ...(customerEmail ? { customer_email: customerEmail } : {}),
      });
      window.location.assign(res.checkout_url);
    } catch (err) {
      setPendingPlan(null);
      toastFromError(err, { title: 'Checkout failed' });
    }
  }

  async function startChangePlan(plan: PlanSlug): Promise<void> {
    const previousPlan = currentPlanSlug;
    try {
      setPendingPlan(plan);
      await changePlan.mutateAsync({ plan });
      // Optimistic invalidations landed inside the mutation; poll for
      // the webhook-driven convergence so the card grid flips without
      // a page reload.
      const converged = await pollUntilPlanChanges({
        qc,
        orgId,
        previousPlan,
      });
      if (converged) {
        toastSuccess('Plan updated', `You're now on the ${plan} plan.`);
      } else {
        toastSuccess(
          'Change sent',
          "Polar is processing the update — refresh in a moment if the banner hasn't flipped.",
        );
      }
    } catch (err) {
      toastFromError(err, { title: 'Plan change failed' });
    } finally {
      setPendingPlan(null);
    }
  }

  async function openCustomerPortal(): Promise<void> {
    try {
      const res = await openPortal.mutateAsync();
      window.open(res.portal_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toastFromError(err, { title: 'Portal unavailable' });
    }
  }

  const renewalLabel = (() => {
    const iso = polar?.current_period_end;
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString();
    } catch {
      return null;
    }
  })();

  return (
    <div className="space-y-8">
      {/*
       * Current-plan section is suppressed for first-time orgs (no
       * `plan` on the Supabase row). Showing a "No active plan"
       * banner to a brand-new workspace is both redundant and
       * alarmist — the plan grid below is self-explanatory. We only
       * render the section when we have something real to recap: an
       * active subscription (plan + renewal date + Manage-billing),
       * or a lapsed one that needs a "sub ended" call-out.
       */}
      {!isFirstTime && (
        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--color-ink)]">Current plan</h2>
          {subscription.isLoading || billing.isLoading ? (
            <Skeleton className="h-24 w-full max-w-md" />
          ) : subscription.error || billing.error ? (
            <ErrorState
              title="We couldn't load billing"
              error={(subscription.error ?? billing.error) as Error}
              retry={() => {
                void subscription.refetch();
                void workspace.refetch();
                void usage.refetch();
              }}
            />
          ) : (
            <div
              className={cn(
                'flex flex-col items-start gap-3 rounded-2xl border p-5 sm:flex-row sm:items-center sm:justify-between',
                // Lapsed subs get the warning tint — they had
                // access, they lost it, they may need to act.
                // Subscribed orgs get the neutral paper card.
                isLapsed
                  ? 'border-[var(--color-warning)]/40 bg-[var(--color-warning)]/10'
                  : 'border-[var(--color-line)] bg-[var(--color-paper)]',
              )}
            >
              <div>
                <div className="flex items-center gap-2">
                  <Crown
                    aria-hidden="true"
                    size={20}
                    weight="duotone"
                    className="text-[var(--color-accent-brand)]"
                  />
                  <h3 className="text-lg font-semibold capitalize text-[var(--color-ink)]">
                    {subscription.data?.plan ?? billing.plan}
                  </h3>
                </div>
                <p className="mt-1 text-sm text-[var(--color-mute)]">
                  {isLapsed
                    ? 'Your subscription has ended. Choose a plan below to restore access.'
                    : `Workspace: ${activeOrg?.name ?? '—'}.`}
                </p>
                {isSubscribed && renewalLabel && (
                  <p className="mt-1 text-xs text-[var(--color-mute)]">
                    Renews {renewalLabel}
                    {polar?.cancel_at_period_end ? ' — scheduled to cancel at period end.' : '.'}
                  </p>
                )}
              </div>
              {isSubscribed && (
                <Button
                  variant="outline"
                  onClick={openCustomerPortal}
                  loading={openPortal.isPending}
                >
                  <ArrowSquareOut aria-hidden="true" />
                  Manage billing
                </Button>
              )}
            </div>
          )}
        </section>
      )}

      {usage.data && (
        <section className="space-y-3">
          <header>
            <h2 className="text-base font-semibold text-[var(--color-ink)]">Usage this month</h2>
            <p className="text-sm text-[var(--color-mute)]">
              Usage counters reset on the 1st of each month. On-demand runs above your allowance
              bill at overage rates on plans with metered billing enabled; suspended or hard-capped
              plans block new runs with a 402 until the next cycle.
            </p>
          </header>
          <div className="grid gap-3 sm:grid-cols-3">
            <UsageCard
              title="Audits MTD"
              value={usage.data.mtd_on_demand}
              limit={usage.data.mtd_on_demand_limit}
            />
            <UsageCard
              title="Monitors"
              value={usage.data.active_daily_monitors + usage.data.active_hourly_monitors}
              limit={
                usage.data.active_daily_monitors_limit != null ||
                usage.data.active_hourly_monitors_limit != null
                  ? (usage.data.active_daily_monitors_limit ?? 0) +
                    (usage.data.active_hourly_monitors_limit ?? 0)
                  : null
              }
            />
            <UsageCard
              title="Seats"
              value={usage.data.members_count}
              limit={usage.data.members_limit}
            />
          </div>
          {usage.data.overage_allowed && usage.data.overage_units > 0 && (
            <p className="text-xs text-[var(--color-mute)]">
              {usage.data.overage_units.toLocaleString()} overage
              {usage.data.overage_units === 1 ? ' audit' : ' audits'} this month
              {usage.data.mtd_source === 'outbox_fallback'
                ? ' (billing feed catching up — number may lag)'
                : ''}
              .
            </p>
          )}
        </section>
      )}

      <section className="space-y-3">
        <header>
          <h2 className="text-base font-semibold text-[var(--color-ink)]">Plans</h2>
          <p className="text-sm text-[var(--color-mute)]">
            Prices shown in USD. Plan changes take effect immediately — upgrades bill the prorated
            difference now, downgrades issue a credit on your next invoice.
          </p>
        </header>
        <ul className="flex flex-col gap-3">
          {plans.map((p) => (
            <PlanCard
              key={p.id}
              plan={p}
              active={currentPlanSlug === p.id}
              currentPlanSlug={currentPlanSlug}
              isSubscribed={isSubscribed}
              pendingPlan={pendingPlan}
              onCheckout={() => startCheckout(p.id)}
              onChangePlan={() => startChangePlan(p.id)}
            />
          ))}
        </ul>
      </section>
    </div>
  );
}

// ─── PlanCard ────────────────────────────────────────────────────────

function PlanCard({
  plan,
  active,
  currentPlanSlug,
  isSubscribed,
  pendingPlan,
  onCheckout,
  onChangePlan,
}: {
  plan: BillingPlanOption;
  active: boolean;
  currentPlanSlug: string | null;
  isSubscribed: boolean;
  pendingPlan: PlanSlug | null;
  onCheckout: () => void;
  onChangePlan: () => void;
}): ReactElement {
  return (
    <li
      className={cn(
        'flex flex-col gap-4 rounded-2xl border p-5 md:flex-row md:items-start md:gap-6',
        active
          ? 'border-[var(--color-accent-brand)] bg-[var(--color-accent-brand)]/5'
          : 'border-[var(--color-line)] bg-[var(--color-paper)]',
      )}
      data-plan={plan.id}
    >
      <div className="md:w-52 md:shrink-0">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-[var(--color-ink)]">{plan.name}</h3>
          {active && (
            <span className="rounded-full bg-[var(--color-accent-brand)] px-2 py-0.5 text-xs font-medium text-white">
              Current
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-[var(--color-mute)]">{plan.description}</p>
        <div className="mt-2 text-xl font-semibold text-[var(--color-ink)]">{plan.price_label}</div>
      </div>

      <ul className="grid flex-1 min-w-0 gap-x-4 gap-y-1 text-sm text-[var(--color-ink-soft)] sm:grid-cols-2">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <CheckCircle
              aria-hidden="true"
              size={14}
              className="mt-0.5 shrink-0 text-[var(--color-success)]"
            />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="md:w-44 md:shrink-0 md:text-right">
        <PlanCta
          plan={plan}
          active={active}
          currentPlanSlug={currentPlanSlug}
          isSubscribed={isSubscribed}
          pending={pendingPlan === plan.id}
          disabled={pendingPlan !== null && pendingPlan !== plan.id}
          onCheckout={onCheckout}
          onChangePlan={onChangePlan}
        />
      </div>
    </li>
  );
}

/**
 * CTA routing:
 *
 *   - `active`           → disabled "Current plan" button.
 *   - `cta: 'contact'`   → `mailto:` (Business tier, no Polar
 *                          self-serve). Active Business customers
 *                          see the same Contact CTA.
 *   - No subscription    → "Choose X" runs `POST /checkout` →
 *                          window.location to Polar.
 *   - Active sub         → "Upgrade" / "Downgrade" runs
 *                          `POST /change-plan` → poll for webhook.
 *   - Lapsed sub (status canceled / past_due, `subscription_id`
 *                          still set) — we route like "no
 *                          subscription" because Polar won't allow
 *                          change-plan on a dead sub; customer
 *                          must go through checkout again.
 */
function PlanCta({
  plan,
  active,
  currentPlanSlug,
  isSubscribed,
  pending,
  disabled,
  onCheckout,
  onChangePlan,
}: {
  plan: BillingPlanOption;
  active: boolean;
  currentPlanSlug: string | null;
  isSubscribed: boolean;
  pending: boolean;
  disabled: boolean;
  onCheckout: () => void;
  onChangePlan: () => void;
}): ReactElement {
  if (active) {
    return (
      <Button variant="outline" className="w-full md:w-auto" disabled>
        Current plan
      </Button>
    );
  }

  if (plan.cta === 'contact') {
    return (
      <Button
        variant="outline"
        className="w-full md:w-auto"
        render={
          // biome-ignore lint/a11y/useAnchorContent: content lives in the Button children which Base UI merges into the rendered <a>
          <a href="mailto:sales@clickweave.co" target="_blank" rel="noreferrer noopener" />
        }
      >
        Contact sales
      </Button>
    );
  }

  const direction = planChangeDirection(currentPlanSlug, plan.id);
  const isDowngrade = direction === 'downgrade';

  // Route to checkout vs change-plan based on whether there's an
  // *active* Polar subscription, not just a non-null plan column —
  // a lapsed sub must go through checkout again.
  const onClick = isSubscribed ? onChangePlan : onCheckout;
  const label = pending
    ? isSubscribed
      ? 'Changing plan…'
      : 'Starting checkout…'
    : isSubscribed
      ? isDowngrade
        ? 'Downgrade'
        : 'Upgrade →'
      : `Choose ${plan.name}`;

  return (
    <Button
      variant={isSubscribed && isDowngrade ? 'outline' : 'default'}
      className="w-full md:w-auto"
      onClick={onClick}
      loading={pending}
      disabled={disabled}
    >
      {label}
    </Button>
  );
}

// ─── Plan-change polling ─────────────────────────────────────────────

/**
 * Bounded poll for the server-reported plan to flip after a
 * `change-plan` call. The Polar webhook → Supabase write usually
 * lands in under 10 s; we give it 15 s before falling back to a
 * "refresh in a moment" toast.
 *
 * Returns `true` when the plan converged, `false` on timeout.
 *
 * Signal: any change in the cached `/v1/usage` plan vs what we saw
 * at mutation start. Works for both upgrade (indie → pro) and
 * downgrade (pro → indie). We don't compare against Clerk's session
 * claim anymore — Phase 6 removed that dependency entirely.
 */
async function pollUntilPlanChanges(params: {
  qc: QueryClient;
  orgId: string | null;
  previousPlan: string | null;
}): Promise<boolean> {
  const { qc, orgId, previousPlan } = params;
  const MAX_ATTEMPTS = 15;
  const INTERVAL_MS = 1_000;

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    await sleep(INTERVAL_MS);
    const cached = qc.getQueryData<UsageResponse>(usageKeys.byOrg(orgId));
    if (cached && (cached.plan ?? null) !== previousPlan) {
      void invalidateWorkspace(qc);
      void invalidateSubscription(qc);
      return true;
    }
    await invalidateUsage(qc);
    await invalidateWorkspace(qc);
    await invalidateSubscription(qc);
  }
  return false;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
